
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ContactForm } from "./components/ContactForm";
import { CookieBanner } from "./components/CookieBanner";
import { LegalModal } from "./components/LegalModal";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  Phone,
  Mail,
  MapPin,
  Utensils,
  Music,
  Trees,
  Users,
  CalendarCheck,
  CheckCircle2,
  ArrowRight,
  Menu,
  Facebook,
  Instagram,
  Heart,
  Briefcase,
  Cake,
  Flower2,
  HelpCircle
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// User provided images
const IMG_EXTERIOR = "https://customer-assets.emergentagent.com/job_feiern-waldhotel/artifacts/kjf3vbjs_4d1fb08c-e96a-4b06-9b14-c3a83bac4a27.jpeg"; // Hero & Garden
const IMG_DETAIL_VASE = "https://customer-assets.emergentagent.com/job_feiern-waldhotel/artifacts/3ad1q1ql_c086ff57-0af3-4c39-bd7e-6ed6ce782b2d.jpeg"; // Detail
const IMG_TABLE_SETTING = "https://customer-assets.emergentagent.com/job_feiern-waldhotel/artifacts/96zgi2qs_1cc39a0d-a3a4-439a-8e03-d02556ece1c9.jpeg"; // Restaurant
const IMG_HALL_LONG_TABLE = "https://customer-assets.emergentagent.com/job_feiern-waldhotel/artifacts/vjv6sfla_6eae1d0c-3ed0-4ac7-b9e5-01865abe1350.jpeg"; // Hall
const IMG_CHAMPAGNE = "https://customer-assets.emergentagent.com/job_feiern-waldhotel/artifacts/37ukrm9s_48a1520c-acb9-48fa-a5fb-cf501a435582.jpeg"; // Champagne
const IMG_DINING_GREEN = "https://customer-assets.emergentagent.com/job_feiern-waldhotel/artifacts/haklcx00_d38b18c4-aaa9-49c6-9d29-b22b408b933c.jpeg"; // Dining Green

// Generated images for Occasions
const IMG_WEDDING = "https://images.unsplash.com/photo-1591604466107-ec97de577aff?auto=format&fit=crop&q=80";
const IMG_BIRTHDAY = "https://images.unsplash.com/photo-1544155892-b2b6c64204fc?auto=format&fit=crop&q=80";
const IMG_CORPORATE = "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&q=80";
const IMG_FUNERAL = "https://images.unsplash.com/photo-1568722499526-3ae705b3a11f?auto=format&fit=crop&q=80";

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (id) => {
    setMobileMenuOpen(false); // Close mobile menu if open
    const element = document.getElementById(id);
    if (element) {
      // Offset for fixed header (80px)
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 font-sans text-stone-900 overflow-x-hidden">
      <CookieBanner />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-stone-200 shadow-sm h-20">
        <div className="container mx-auto px-4 h-full flex items-center justify-between">
          <div className="flex items-center gap-2 z-50 relative">
            <span className="text-lg md:text-xl font-serif font-bold text-emerald-950 tracking-tight leading-none">
              Waldhotel <br className="md:hidden" /> Bad Essen
            </span>
          </div>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('about')} className="text-sm font-medium hover:text-emerald-800 transition-colors uppercase tracking-wide">Über uns</button>
            <button onClick={() => scrollToSection('occasions')} className="text-sm font-medium hover:text-emerald-800 transition-colors uppercase tracking-wide">Anlässe</button>
            <button onClick={() => scrollToSection('venues')} className="text-sm font-medium hover:text-emerald-800 transition-colors uppercase tracking-wide">Räumlichkeiten</button>
            <button onClick={() => scrollToSection('faq')} className="text-sm font-medium hover:text-emerald-800 transition-colors uppercase tracking-wide">FAQ</button>
            <button onClick={() => scrollToSection('services')} className="text-sm font-medium hover:text-emerald-800 transition-colors uppercase tracking-wide">Service</button>
            <Button 
              onClick={() => scrollToSection('contact')}
              className="bg-emerald-900 hover:bg-emerald-800 text-white shadow-md hover:shadow-lg transition-all transform hover:-translate-y-0.5"
            >
              Jetzt anfragen
            </Button>
          </div>

          {/* Mobile Nav */}
          <div className="md:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="hover:bg-emerald-50">
                  <Menu className="h-6 w-6 text-emerald-900" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-stone-50 w-[85%] sm:w-[385px] pt-12 overflow-y-auto">
                <div className="flex flex-col gap-6">
                  <button onClick={() => scrollToSection('about')} className="text-xl font-serif font-medium text-left border-b border-stone-200 pb-3 text-emerald-900">Über uns</button>
                  <button onClick={() => scrollToSection('occasions')} className="text-xl font-serif font-medium text-left border-b border-stone-200 pb-3 text-emerald-900">Anlässe</button>
                  <button onClick={() => scrollToSection('venues')} className="text-xl font-serif font-medium text-left border-b border-stone-200 pb-3 text-emerald-900">Räumlichkeiten</button>
                  <button onClick={() => scrollToSection('faq')} className="text-xl font-serif font-medium text-left border-b border-stone-200 pb-3 text-emerald-900">FAQ</button>
                  <button onClick={() => scrollToSection('services')} className="text-xl font-serif font-medium text-left border-b border-stone-200 pb-3 text-emerald-900">Service</button>
                  <Button 
                    onClick={() => scrollToSection('contact')}
                    className="bg-emerald-900 hover:bg-emerald-800 text-white w-full mt-4 py-6 text-lg shadow-lg"
                  >
                    Jetzt anfragen
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMG_EXTERIOR} 
            alt="Waldhotel Bad Essen Außenansicht" 
            className="w-full h-full object-cover transition-transform duration-[30s] hover:scale-105 animate-ken-burns"
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-black/20 to-black/40" />
        </div>
        
        <div className="relative container mx-auto px-4 text-center text-white z-10 max-w-5xl mt-12 md:mt-0 flex flex-col items-center">
          <div className="inline-block py-1.5 px-4 rounded-full bg-emerald-950/60 backdrop-blur-md border border-emerald-500/30 text-emerald-100 text-xs md:text-sm font-medium mb-6 uppercase tracking-widest animate-fade-in-up shadow-lg">
            Willkommen im Teutoburger Wald
          </div>
          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-serif font-bold mb-6 leading-[1.1] md:leading-tight drop-shadow-xl animate-fade-in-up delay-100">
            Unvergessliche Feiern <br className="hidden md:block" />
            <span className="text-emerald-200 italic block md:inline mt-2 md:mt-0">in Bad Essen</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-stone-100 mb-10 max-w-2xl mx-auto leading-relaxed drop-shadow-md animate-fade-in-up delay-200 px-2 sm:px-4 font-light tracking-wide">
            Ob Hochzeit, Jubiläum oder Firmenfeier – erleben Sie magische Momente in unserem historischen Festsaal, dem gemütlichen Restaurant oder im idyllischen Biergarten.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up delay-300 px-4 w-full sm:w-auto">
            <Button 
              size="lg" 
              onClick={() => scrollToSection('contact')}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-lg px-8 py-6 h-auto shadow-xl shadow-emerald-900/40 transition-all hover:-translate-y-1 w-full sm:w-auto font-semibold"
            >
              Termin anfragen
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => scrollToSection('venues')}
              className="bg-white/10 hover:bg-white/20 text-white border-white/40 text-lg px-8 py-6 h-auto backdrop-blur-sm transition-all w-full sm:w-auto font-semibold"
            >
              Räumlichkeiten
            </Button>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden md:block text-white/70">
           <ArrowRight className="h-6 w-6 rotate-90" />
        </div>
      </header>

      {/* Intro Section */}
      <section id="about" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <Utensils className="h-10 w-10 text-emerald-800 mx-auto mb-6 opacity-80" />
            <h2 className="text-2xl md:text-4xl font-serif font-bold text-stone-900 mb-6">
              Ein Ort für besondere Augenblicke
            </h2>
            <p className="text-base md:text-lg text-stone-600 leading-relaxed mb-10 px-4 md:px-0">
              Das Waldhotel Bad Essen verbindet traditionelle Gastfreundschaft mit modernem Komfort. 
              Eingebettet in die malerische Kulisse des Teutoburger Waldes bieten wir den perfekten Rahmen 
              für Ihre Feierlichkeiten. Von intimen Familienfeiern bis hin zu rauschenden Festen mit 
              bis zu 100 Personen – wir machen Ihre Veranstaltung zu einem einzigartigen Erlebnis.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 pt-8 border-t border-stone-100">
              <div className="flex flex-col items-center gap-3 p-4 rounded-xl hover:bg-stone-50 transition-colors group">
                <Users className="h-8 w-8 text-emerald-700 group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-stone-800 text-sm md:text-base">Bis zu 100 Gäste</span>
              </div>
              <div className="flex flex-col items-center gap-3 p-4 rounded-xl hover:bg-stone-50 transition-colors group">
                <Trees className="h-8 w-8 text-emerald-700 group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-stone-800 text-sm md:text-base">Mitten im Grünen</span>
              </div>
              <div className="flex flex-col items-center gap-3 p-4 rounded-xl hover:bg-stone-50 transition-colors group">
                <CalendarCheck className="h-8 w-8 text-emerald-700 group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-stone-800 text-sm md:text-base">Individuelle Planung</span>
              </div>
              <div className="flex flex-col items-center gap-3 p-4 rounded-xl hover:bg-stone-50 transition-colors group">
                <Music className="h-8 w-8 text-emerald-700 group-hover:scale-110 transition-transform" />
                <span className="font-semibold text-stone-800 text-sm md:text-base">Alles aus einer Hand</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Occasions Section */}
      <section id="occasions" className="py-16 md:py-24 bg-stone-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-emerald-800 font-bold tracking-widest uppercase text-xs md:text-sm">Für jeden Anlass</span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mt-3">
              Wir feiern mit Ihnen
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Wedding */}
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group">
              <div className="h-48 overflow-hidden relative">
                <img src={IMG_WEDDING} alt="Hochzeit" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                <div className="absolute top-4 right-4 bg-white/90 p-2 rounded-full text-emerald-800">
                  <Heart className="h-5 w-5 fill-current" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-serif font-bold text-emerald-900 mb-3">Hochzeiten</h3>
                <p className="text-stone-600 text-sm leading-relaxed mb-4">
                  Der schönste Tag im Leben verdient den perfekten Rahmen. Ob freie Trauung im Garten oder festliches Dinner im Saal – wir machen Ihre Träume wahr.
                </p>
                <ul className="text-xs text-stone-500 space-y-2">
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Romantische Atmosphäre</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Sektempfang im Grünen</li>
                </ul>
              </div>
            </div>

            {/* Birthday */}
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group">
              <div className="h-48 overflow-hidden relative">
                <img src={IMG_BIRTHDAY} alt="Geburtstag" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                <div className="absolute top-4 right-4 bg-white/90 p-2 rounded-full text-emerald-800">
                  <Cake className="h-5 w-5 fill-current" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-serif font-bold text-emerald-900 mb-3">Geburtstage</h3>
                <p className="text-stone-600 text-sm leading-relaxed mb-4">
                  Feiern Sie das Leben mit Familie und Freunden. Von der gemütlichen Kaffeetafel bis zur rauschenden Party am Abend mit Tanz und Musik.
                </p>
                <ul className="text-xs text-stone-500 space-y-2">
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Menü oder Buffet</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Platz für Tanzfläche</li>
                </ul>
              </div>
            </div>

            {/* Corporate */}
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group">
              <div className="h-48 overflow-hidden relative">
                <img src={IMG_CORPORATE} alt="Firmenfeier" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                <div className="absolute top-4 right-4 bg-white/90 p-2 rounded-full text-emerald-800">
                  <Briefcase className="h-5 w-5 fill-current" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-serif font-bold text-emerald-900 mb-3">Firmenfeiern</h3>
                <p className="text-stone-600 text-sm leading-relaxed mb-4">
                  Professionell tagen und stilvoll feiern. Unsere Räume bieten die ideale Atmosphäre für Meetings, Weihnachtsfeiern oder Firmenjubiläen.
                </p>
                <ul className="text-xs text-stone-500 space-y-2">
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Beamer & Technik</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Ungestörte Lage</li>
                </ul>
              </div>
            </div>

            {/* Funeral */}
            <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow group">
              <div className="h-48 overflow-hidden relative">
                <img src={IMG_FUNERAL} alt="Trauerfeier" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors" />
                <div className="absolute top-4 right-4 bg-white/90 p-2 rounded-full text-emerald-800">
                  <Flower2 className="h-5 w-5 fill-current" />
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-serif font-bold text-emerald-900 mb-3">Trauerfeiern</h3>
                <p className="text-stone-600 text-sm leading-relaxed mb-4">
                  Ein würdevoller Abschied in ruhiger Atmosphäre. Wir kümmern uns diskret um den Rahmen bei Kaffee und Kuchen, damit Sie Zeit für Erinnerungen haben.
                </p>
                <ul className="text-xs text-stone-500 space-y-2">
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Pietätvoller Service</li>
                  <li className="flex items-center gap-2"><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Separater Bereich</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Venues Section */}
      <section id="venues" className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-emerald-800 font-bold tracking-widest uppercase text-xs md:text-sm">Unsere Locations</span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mt-3">
              Räumlichkeiten zum Wohlfühlen
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* Hall */}
            <Card className="border-none shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300">
              <div className="h-64 overflow-hidden relative">
                <img 
                  src={IMG_HALL_LONG_TABLE} 
                  alt="Festsaal mit langer Tafel" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-xs font-bold uppercase tracking-wider mb-1 text-emerald-300">Festlich</p>
                  <h3 className="font-serif text-2xl font-bold">Der Festsaal</h3>
                </div>
              </div>
              <CardContent className="pt-6 bg-stone-50">
                <p className="text-stone-600 mb-6 text-sm md:text-base leading-relaxed">
                  Unser prachtvoller Saal bietet Platz für große Gesellschaften. 
                  Mit eleganter Beleuchtung und viel Raum für lange Tafeln ist er 
                  das Herzstück für Hochzeiten und Jubiläen.
                </p>
                <ul className="text-sm text-stone-500 space-y-3">
                  <li className="flex items-center gap-2"><div className="bg-emerald-100 p-1 rounded-full"><CheckCircle2 className="h-3 w-3 text-emerald-700" /></div> Bis zu 100 Personen</li>
                  <li className="flex items-center gap-2"><div className="bg-emerald-100 p-1 rounded-full"><CheckCircle2 className="h-3 w-3 text-emerald-700" /></div> Flexible Bestuhlung</li>
                </ul>
              </CardContent>
            </Card>

            {/* Restaurant */}
            <Card className="border-none shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300">
              <div className="h-64 overflow-hidden relative">
                <img 
                  src={IMG_DINING_GREEN} 
                  alt="Restaurant Tischdekoration" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                 <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-xs font-bold uppercase tracking-wider mb-1 text-emerald-300">Gemütlich</p>
                  <h3 className="font-serif text-2xl font-bold">Das Restaurant</h3>
                </div>
              </div>
              <CardContent className="pt-6 bg-stone-50">
                <p className="text-stone-600 mb-6 text-sm md:text-base leading-relaxed">
                  Genießen Sie die warme Atmosphäre unseres Restaurants. 
                  Ideal für kleinere Feiern, Familienessen oder ein gemütliches Beisammensein 
                  in stilvollem Ambiente mit Blick ins Grüne.
                </p>
                <ul className="text-sm text-stone-500 space-y-3">
                  <li className="flex items-center gap-2"><div className="bg-emerald-100 p-1 rounded-full"><CheckCircle2 className="h-3 w-3 text-emerald-700" /></div> Intime Atmosphäre</li>
                  <li className="flex items-center gap-2"><div className="bg-emerald-100 p-1 rounded-full"><CheckCircle2 className="h-3 w-3 text-emerald-700" /></div> Exzellente Küche</li>
                </ul>
              </CardContent>
            </Card>

            {/* Beer Garden */}
            <Card className="border-none shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-300">
              <div className="h-64 overflow-hidden relative">
                <img 
                  src={IMG_EXTERIOR} // Using exterior shot again as it shows garden area well
                  alt="Biergarten Außenbereich" 
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                 <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60 group-hover:opacity-40 transition-opacity" />
                <div className="absolute bottom-4 left-4 text-white">
                  <p className="text-xs font-bold uppercase tracking-wider mb-1 text-emerald-300">Idyllisch</p>
                  <h3 className="font-serif text-2xl font-bold">Der Biergarten</h3>
                </div>
              </div>
              <CardContent className="pt-6 bg-stone-50">
                <p className="text-stone-600 mb-6 text-sm md:text-base leading-relaxed">
                  Feiern Sie unter freiem Himmel, umgeben von der Natur des Wiehengebirges. 
                  Unser Biergarten ist der perfekte Ort für Sommerfeste, Sektempfänge 
                  oder ein entspanntes BBQ.
                </p>
                <ul className="text-sm text-stone-500 space-y-3">
                  <li className="flex items-center gap-2"><div className="bg-emerald-100 p-1 rounded-full"><CheckCircle2 className="h-3 w-3 text-emerald-700" /></div> Mitten in der Natur</li>
                  <li className="flex items-center gap-2"><div className="bg-emerald-100 p-1 rounded-full"><CheckCircle2 className="h-3 w-3 text-emerald-700" /></div> Grill-Events möglich</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-24 bg-stone-50 relative overflow-hidden">
        {/* Decorative champagne image on larger screens */}
        <div className="absolute top-0 right-0 h-full w-1/3 opacity-5 pointer-events-none hidden lg:block">
            <img src={IMG_CHAMPAGNE} alt="Champagner Empfang" className="h-full w-full object-cover" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
             <div className="text-center mb-12">
                <span className="text-emerald-800 font-bold tracking-widest uppercase text-xs md:text-sm">Wissenswertes</span>
                <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mt-3">
                  Häufige Fragen
                </h2>
              </div>

            <div className="bg-white rounded-2xl shadow-xl p-8 border border-stone-100">
               <Accordion type="single" collapsible className="w-full">
                
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-lg font-serif font-medium text-emerald-900 hover:text-emerald-700 hover:no-underline px-4">
                    Bis wie viel Uhr darf gefeiert werden (Sperrstunde)?
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 leading-relaxed px-4 text-base">
                    Es gibt bei uns keine feste Sperrstunde. Da wir jedoch Rücksicht auf unsere Nachbarn und Anwohner nehmen, sollte die Feier ab 23:00 Uhr in die Innenräume verlagert werden. Drinnen können Sie dann ungestört und ausgelassen weiterfeiern.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-lg font-serif font-medium text-emerald-900 hover:text-emerald-700 hover:no-underline px-4">
                    Darf eigenes Dekorationsmaterial mitgebracht werden?
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 leading-relaxed px-4 text-base">
                    Ja, selbstverständlich! Sie können gerne Ihre eigene Dekoration mitbringen, um den Räumen Ihre persönliche Note zu verleihen. Sprechen Sie uns einfach auf Ihre Vorstellungen an, damit wir die Details gemeinsam abstimmen können.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-lg font-serif font-medium text-emerald-900 hover:text-emerald-700 hover:no-underline px-4">
                    Gibt es ausreichend Parkplätze direkt am Haus?
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 leading-relaxed px-4 text-base">
                    Ja, wir verfügen über 140 kostenfreie Parkplätze direkt am Hotel. Ihre Gäste können also bequem anreisen und müssen sich keine Sorgen um die Parkplatzsuche machen.
                  </AccordionContent>
                </AccordionItem>
                
                 <AccordionItem value="item-4">
                  <AccordionTrigger className="text-lg font-serif font-medium text-emerald-900 hover:text-emerald-700 hover:no-underline px-4">
                    Können meine Gäste im Hotel übernachten?
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 leading-relaxed px-4 text-base">
                     Absolut. Das Waldhotel verfügt über 19 individuell und gemütlich eingerichtete Zimmer. So können Ihre Gäste die Feier entspannt ausklingen lassen und am nächsten Morgen ein gemeinsames Frühstück genießen. Wir empfehlen eine rechtzeitige Reservierung.
                  </AccordionContent>
                </AccordionItem>

                <AccordionItem value="item-5">
                  <AccordionTrigger className="text-lg font-serif font-medium text-emerald-900 hover:text-emerald-700 hover:no-underline px-4">
                    Bieten Sie auch vegetarische oder vegane Menüs an?
                  </AccordionTrigger>
                  <AccordionContent className="text-stone-600 leading-relaxed px-4 text-base">
                    Ja, sehr gerne. Unser Küchenteam geht flexibel auf individuelle Ernährungswünsche und Unverträglichkeiten ein. Wir stellen Ihnen gerne köstliche vegetarische und vegane Alternativen für Ihr Menü oder Buffet zusammen.
                  </AccordionContent>
                </AccordionItem>

              </Accordion>
            </div>
            
             <div className="mt-8 text-center text-stone-500 text-sm">
                <p>Haben Sie weitere Fragen? Kontaktieren Sie uns gerne direkt.</p>
                <Button 
                    variant="link" 
                    onClick={() => scrollToSection('contact')}
                    className="text-emerald-700 font-semibold hover:text-emerald-900 p-0 h-auto mt-2"
                  >
                    Zur Kontaktanfrage <ArrowRight className="inline-block h-3 w-3 ml-1" />
                </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 md:py-24 bg-emerald-950 text-white relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-emerald-800 rounded-full blur-[100px] opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-600 rounded-full blur-[120px] opacity-10 translate-x-1/3 translate-y-1/3"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <span className="text-emerald-300 font-bold tracking-widest uppercase text-xs md:text-sm">Unsere Angebote</span>
            <h2 className="text-3xl md:text-5xl font-serif font-bold mt-3">
              Maßgeschneidert für Sie
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Room Rental Only */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors flex flex-col h-full">
              <h3 className="text-2xl font-serif font-bold mb-2 text-emerald-200">Reine Raummiete</h3>
              <p className="text-stone-300 mb-6 flex-grow">Sie möchten alles selbst organisieren? Mieten Sie einfach unsere Räumlichkeiten.</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="bg-emerald-900/50 p-1 rounded-full mt-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" /></div>
                  <span className="text-stone-200 text-sm">Nutzung des gewählten Raumes</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-emerald-900/50 p-1 rounded-full mt-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" /></div>
                  <span className="text-stone-200 text-sm">Grundreinigung inklusive</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-emerald-900/50 p-1 rounded-full mt-1"><CheckCircle2 className="h-3 w-3 text-emerald-400" /></div>
                  <span className="text-stone-200 text-sm">Freie Wahl bei Deko & Musik</span>
                </li>
              </ul>
              <Button onClick={() => scrollToSection('contact')} variant="outline" className="w-full text-emerald-100 border-emerald-500/50 hover:bg-emerald-500/20 hover:text-white bg-transparent h-12">
                Raum anfragen
              </Button>
            </div>

            {/* Full Service */}
            <div className="bg-gradient-to-br from-emerald-800 to-emerald-900 border border-emerald-600 rounded-2xl p-8 shadow-xl transform md:-translate-y-4 flex flex-col h-full relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4">
                 <div className="bg-amber-400 text-amber-950 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wide shadow-md">
                  Empfohlen
                </div>
              </div>
              <h3 className="text-2xl font-serif font-bold mb-2 text-white">Full-Service Planung</h3>
              <p className="text-emerald-100 mb-6 flex-grow">Lehnen Sie sich zurück. Wir kümmern uns um jedes Detail Ihrer Feier.</p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <div className="bg-white/20 p-1 rounded-full mt-1"><CheckCircle2 className="h-3 w-3 text-amber-300" /></div>
                  <span className="text-white text-sm">Exzellentes Catering (Menü/Buffet)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-white/20 p-1 rounded-full mt-1"><CheckCircle2 className="h-3 w-3 text-amber-300" /></div>
                  <span className="text-white text-sm">Professioneller Service am Tisch</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="bg-white/20 p-1 rounded-full mt-1"><CheckCircle2 className="h-3 w-3 text-amber-300" /></div>
                  <span className="text-white text-sm">Festliche Dekoration nach Wunsch</span>
                </li>
                 <li className="flex items-start gap-3">
                  <div className="bg-white/20 p-1 rounded-full mt-1"><CheckCircle2 className="h-3 w-3 text-amber-300" /></div>
                  <span className="text-white text-sm">Vermittlung von DJ & Technik</span>
                </li>
              </ul>
              <Button onClick={() => scrollToSection('contact')} className="w-full bg-amber-500 hover:bg-amber-400 text-amber-950 font-bold border-none h-12 text-base">
                Rundum-Sorglos anfragen
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-24 bg-stone-100">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <span className="text-emerald-800 font-bold tracking-widest uppercase text-xs md:text-sm">Kontaktieren Sie uns</span>
                <h2 className="text-3xl md:text-5xl font-serif font-bold text-stone-900 mt-3 mb-6">
                  Wir freuen uns auf Ihre Feier
                </h2>
                <p className="text-lg text-stone-600 mb-8 leading-relaxed">
                  Haben Sie Fragen oder möchten Sie einen Termin vereinbaren? 
                  Füllen Sie einfach das Formular aus oder schreiben Sie uns direkt per WhatsApp.
                </p>
              </div>

              <div className="space-y-4">
                <a 
                  href="https://wa.me/4917632419623" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-sm border border-stone-200 hover:shadow-md transition-all group hover:border-emerald-200"
                >
                  <div className="bg-[#25D366] p-3 rounded-full text-white group-hover:scale-110 transition-transform shadow-sm">
                    <Phone className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-900 text-lg">WhatsApp Direkt-Chat</h3>
                    <p className="text-stone-500 font-medium">+49 176 3241 9623</p>
                  </div>
                  <ArrowRight className="ml-auto h-5 w-5 text-stone-300 group-hover:text-emerald-600 group-hover:translate-x-1 transition-all" />
                </a>

                <a 
                  href="mailto:kontakt@waldhotel-badessen.de" 
                  className="flex items-center gap-4 p-6 bg-white rounded-xl shadow-sm border border-stone-200 hover:shadow-md transition-all group hover:border-stone-400"
                >
                  <div className="bg-stone-800 p-3 rounded-full text-white group-hover:scale-110 transition-transform shadow-sm">
                    <Mail className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-900 text-lg">E-Mail Anfrage</h3>
                    <p className="text-stone-500 font-medium">kontakt@waldhotel-badessen.de</p>
                  </div>
                  <ArrowRight className="ml-auto h-5 w-5 text-stone-300 group-hover:text-stone-800 group-hover:translate-x-1 transition-all" />
                </a>
              </div>
              
               <div className="bg-emerald-50 p-6 rounded-xl border border-emerald-100 mt-8">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-emerald-700 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-emerald-900 mb-1">Waldhotel Bad Essen</h3>
                    <p className="text-emerald-800">Bergstraße 51<br/>49152 Bad Essen</p>
                  </div>
                </div>
              </div>

              {/* Detail Image */}
               <div className="hidden lg:block rounded-xl overflow-hidden shadow-lg h-64 mt-8 relative group">
                 <img src={IMG_DETAIL_VASE} alt="Detail" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                 <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors"></div>
               </div>
            </div>

            {/* Form */}
            <div className="relative">
               {/* Decorative elements behind form */}
               <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-200 rounded-full blur-3xl opacity-30"></div>
               <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-emerald-200 rounded-full blur-3xl opacity-30"></div>
               
               <div className="relative z-10">
                 <ContactForm />
               </div>
            </div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-12 md:py-16 border-t border-stone-800 text-sm pb-24 md:pb-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-1">
              <span className="text-xl font-serif font-bold text-white mb-6 block">Waldhotel Bad Essen</span>
              <p className="text-stone-400 leading-relaxed mb-6">
                Ihr Partner für unvergessliche Momente im Teutoburger Wald. 
                Tradition, Natur und herzliche Gastfreundschaft seit 1906.
              </p>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/WaldhotelBadEssen/" target="_blank" rel="noopener noreferrer" className="bg-stone-800 p-2 rounded-full hover:bg-emerald-800 hover:text-white transition-all"><Facebook className="h-4 w-4" /></a>
                <a href="https://www.instagram.com/waldhotel_badessen/" target="_blank" rel="noopener noreferrer" className="bg-stone-800 p-2 rounded-full hover:bg-emerald-800 hover:text-white transition-all"><Instagram className="h-4 w-4" /></a>
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-xs">Kontakt</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 mt-0.5 text-emerald-500 flex-shrink-0" />
                  <span>Bergstraße 51<br/>49152 Bad Essen</span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  <span>+49 176 3241 9623</span>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  <span>kontakt@waldhotel-badessen.de</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-xs">Anlässe</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('occasions')} className="hover:text-emerald-400 transition-colors block py-1 text-left">Hochzeiten</button></li>
                <li><button onClick={() => scrollToSection('occasions')} className="hover:text-emerald-400 transition-colors block py-1 text-left">Geburtstage</button></li>
                <li><button onClick={() => scrollToSection('occasions')} className="hover:text-emerald-400 transition-colors block py-1 text-left">Firmenfeiern</button></li>
                <li><button onClick={() => scrollToSection('occasions')} className="hover:text-emerald-400 transition-colors block py-1 text-left">Trauerfeiern</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4 uppercase tracking-wider text-xs">Rechtliches</h4>
              <ul className="space-y-2">
                <li><LegalModal triggerText="Impressum" type="impressum" /></li>
                <li><LegalModal triggerText="Datenschutz" type="datenschutz" /></li>
                <li><a href="#" className="hover:text-white transition-colors block py-1">AGB</a></li>
                <li><button onClick={() => localStorage.removeItem('cookieConsent') || window.location.reload()} className="hover:text-white transition-colors text-left block py-1">Cookie-Einstellungen</button></li>
              </ul>
            </div>
          </div>
          
          <Separator className="bg-stone-800 mb-8" />
          
          <div className="flex flex-col md:flex-row justify-between items-center text-xs text-stone-600 gap-4">
            <p>&copy; {new Date().getFullYear()} Waldhotel Bad Essen UG. Alle Rechte vorbehalten.</p>
            <p className="flex items-center gap-1">Designed with <span className="text-red-900">❤️</span> in Bad Essen</p>
          </div>
        </div>
      </footer>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
